// Settings → Qualifications: the council-maintained specialized roles/
// certifications registry (NCAP, Shooting Sports, ...). Organized by area
// first, people underneath — never a flat person-centric list (see
// lib/qualifications.ts's doc comments for the full rationale). Follows the
// same extracted-large-component precedent as PermissionsMatrix.tsx.

import { useEffect, useState } from 'react';
import { useAppContext } from './Layout';
import {
  useVisibleQualificationAreas,
  useVisibleQualificationAssignments,
} from '../hooks/useScopedData';
import { useQualificationPeople, qualificationPersonContext } from '../hooks/useQualificationPeople';
import { canManageQualifications } from '../lib/visibility';
import {
  findRoleType,
  groupAssignmentsByPerson,
  heldTypeIds,
  qualificationExpiration,
  roleTypeName,
  qualificationTypeName,
  type PersonAreaAssignments,
} from '../lib/qualifications';
import {
  createQualificationArea,
  updateQualificationArea,
  grantQualification,
  updateQualificationExpiration,
  revokeQualification,
} from '../lib/db';
import { newId } from '../types';
import type { QualificationPerson, QualificationArea, QualificationAssignment } from '../types';
import Modal from './Modal';
import Spinner from './Spinner';


/** 'YYYY-MM-DD' → "Sep 16, 2026" — never show the raw ISO form to a user. */
function fmtQualDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export default function QualificationsSettings() {
  const { profile } = useAppContext();
  const areasQ = useVisibleQualificationAreas(profile);
  const assignmentsQ = useVisibleQualificationAssignments(profile);
  const peopleQ = useQualificationPeople(profile, assignmentsQ.data.map(a => a.personId).sort().join(','));
  const canManage = canManageQualifications(profile);
  const [addingArea, setAddingArea] = useState(false);

  const loading = areasQ.loading || assignmentsQ.loading || peopleQ.loading;
  const areas = [...areasQ.data].sort((a, b) => a.name.localeCompare(b.name));

  return (
    <>
      <section className="card set-card">
        <div className="set-cardhead">
          <div>
            <h2>Qualifications</h2>
            <div className="subtitle">
              Council-maintained specialized roles, qualifications, and certifications —
              organized by program area, with the people who hold them underneath. This is
              separate from the National training data Compass already imports (Trained
              Leaders, Safeguarding Youth Training, etc.) — it's for specialized,
              council-tracked certifications those reports don't cover.
            </div>
          </div>
          {canManage && (
            <button className="btn small" onClick={() => setAddingArea(true)}>
              + Add area
            </button>
          )}
        </div>
      </section>

      {peopleQ.error && <div className="card" role="alert">Unable to load Qualification people: {peopleQ.error.message}</div>}
      {loading && (
        <div className="card" style={{ padding: 16 }}>
          <Spinner label="Loading qualifications…" />
        </div>
      )}

      {!loading && areas.length === 0 && (
        <div className="card set-empty" style={{ padding: 16 }}>
          No qualification areas yet.
          {canManage ? ' Use "+ Add area" above to create the first one (e.g. NCAP).' : ''}
        </div>
      )}

      {!loading &&
        areas.map((area) => (
          <QualificationAreaSection
            key={area.id}
            area={area}
            assignments={assignmentsQ.data.filter((a) => a.areaId === area.id)}
            people={peopleQ.data}
            canManage={canManage}
          />
        ))}

      {addingArea && (
        <AreaDialog
          title="Add qualification area"
          initialName=""
          initialDescription=""
          onApply={async (name, description) => {
            await createQualificationArea({ name, description });
          }}
          onClose={() => setAddingArea(false)}
        />
      )}
    </>
  );
}

// ---- Area section -----------------------------------------------------

function QualificationAreaSection({
  area,
  assignments,
  people,
  canManage,
}: {
  area: QualificationArea;
  assignments: QualificationAssignment[];
  people: QualificationPerson[];
  canManage: boolean;
}) {
  const [search, setSearch] = useState('');
  const [managingTypes, setManagingTypes] = useState(false);
  const [editingArea, setEditingArea] = useState(false);
  const [addingPerson, setAddingPerson] = useState(false);
  const [grantFor, setGrantFor] = useState<{ personId: string; personName: string } | null>(null);
  const [editingExpiration, setEditingExpiration] = useState<QualificationAssignment | null>(null);
  const [managingPerson, setManagingPerson] = useState<string | null>(null);

  const peopleById = new Map(people.map((c) => [c.personId, c]));
  const groups = groupAssignmentsByPerson(assignments)
    .map((entry) => ({ entry, person: peopleById.get(entry.personId) ?? { personId: entry.personId, displayName: 'Person unavailable — requires review' } }))
    .filter(
      (row) => !search.trim() || row.person.displayName.toLowerCase().includes(search.trim().toLowerCase()),
    )
    .sort((a, b) => a.person.displayName.localeCompare(b.person.displayName));

  const rosterPersonIds = new Set(assignments.map((a) => a.personId));
  const hasTypes = area.roleTypes.length > 0 || area.qualificationTypes.length > 0;

  return (
    <section className="card set-card">
      <div className="set-cardhead">
        <div>
          <h2>{area.name}</h2>
          {area.systemKey && <div className="subtitle">System Area · display name and types are editable; system identity is fixed.</div>}
          {area.description && <div className="subtitle">{area.description}</div>}
        </div>
        {canManage && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="linkbtn" onClick={() => setEditingArea(true)}>
              Edit
            </button>
            <button className="linkbtn" onClick={() => setManagingTypes((v) => !v)}>
              {managingTypes ? 'Done managing types' : 'Manage types'}
            </button>
            {hasTypes && (
              <button className="linkbtn" onClick={() => setAddingPerson(true)}>
                + Add person
              </button>
            )}
          </div>
        )}
      </div>

      {managingTypes && canManage && <TypesManager area={area} assignments={assignments} />}

      <div style={{ padding: '12px 16px', borderTop: '1px solid var(--line)' }}>
        <input
          placeholder={`Search people in ${area.name}…`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ marginBottom: 10 }}
        />

        {!hasTypes && (
          <div className="subtitle" style={{ marginBottom: 10 }}>
            {canManage
              ? 'No role or qualification types defined yet — use "Manage types" above to add one (e.g. "Council NCAP Chair" or "Short-Term Camp Assessor") before assigning people.'
              : 'No role or qualification types defined for this area yet.'}
          </div>
        )}

        <div className="set-list">
          {groups.map(({ entry, person }) => (
            <RosterRow
              key={person.personId}
              area={area}
              person={person}
              entry={entry}
              canManage={canManage}
              onManage={() => setManagingPerson(person.personId)}
            />
          ))}
          {groups.length === 0 && (
            <div className="set-row set-empty">
              {search.trim() ? 'No one matches that search.' : 'No one assigned to this area yet.'}
            </div>
          )}
        </div>

      </div>

      {editingArea && (
        <AreaDialog
          title={`Edit ${area.name}`}
          initialName={area.name}
          initialDescription={area.description ?? ''}
          onApply={async (name, description) => {
            await updateQualificationArea(area.id, { name, description });
          }}
          onClose={() => setEditingArea(false)}
        />
      )}

      {managingPerson && !grantFor && !editingExpiration && (
        <ManagePersonDialog
          person={peopleById.get(managingPerson) ?? { personId: managingPerson, displayName: 'Person unavailable — requires review' }}
          area={area}
          entry={groupAssignmentsByPerson(assignments).find(e => e.personId === managingPerson)}
          onAdd={() => setGrantFor({ personId: managingPerson, personName: peopleById.get(managingPerson)?.displayName ?? 'Person unavailable — requires review' })}
          onEditExpiration={setEditingExpiration}
          onClose={() => setManagingPerson(null)}
        />
      )}

      {grantFor && (
        <GrantDialog
          area={area}
          personId={grantFor.personId}
          personName={grantFor.personName}
          heldRoleIds={heldTypeIds(
            groups.find((g) => g.person.personId === grantFor.personId)?.entry,
            'role',
          )}
          heldQualificationIds={heldTypeIds(
            groups.find((g) => g.person.personId === grantFor.personId)?.entry,
            'qualification',
          )}
          onClose={() => setGrantFor(null)}
        />
      )}

      {editingExpiration && (
        <ExpirationDialog assignment={editingExpiration} onClose={() => setEditingExpiration(null)} />
      )}

      {addingPerson && (
        <AddPersonDialog
          areaName={area.name}
          people={people.filter((c) => !rosterPersonIds.has(c.personId))}
          onPick={(c) => {
            setAddingPerson(false);
            setGrantFor({ personId: c.personId, personName: c.displayName });
          }}
          onClose={() => setAddingPerson(false)}
        />
      )}
    </section>
  );
}

// ---- Roster row ----------------------------------------------------------

export function RosterRow({ area, person, entry, canManage, onManage }: {
  area: QualificationArea; person: QualificationPerson; entry: PersonAreaAssignments;
  canManage: boolean; onManage: () => void;
}) {
  return (
    <div className="set-row qualification-person" style={{ alignItems: 'flex-start' }}>
      <div className="set-row-main">
        <div className="set-name">
          {person.displayName}
          {entry.roles.length === 1 && <> — {roleTypeName(area, entry.roles[0].typeId)}</>}
          {entry.roles.length > 1 && <span style={{ color: 'var(--danger)' }}> — Area roles require review</span>}
        </div>
        <div className="subtitle" style={{ marginTop: 2 }}>{person.districtNames?.length ? person.districtNames.join(' · ') : 'Council'}</div>
        <div style={{ marginTop: 6, display: 'grid', gap: 4 }}>
          {entry.qualifications.map(a => <div key={a.id}><AssignmentLine label={qualificationTypeName(area, a.typeId)} assignment={a} /></div>)}
        </div>
      </div>
      {canManage && <button className="linkbtn" onClick={onManage}>Manage</button>}
    </div>
  );
}

function AssignmentLine({ label, assignment }: { label: string; assignment: QualificationAssignment }) {
  const exp = qualificationExpiration(assignment.expiresOn);
  return <>{label}{assignment.expiresOn && <> — <span style={{
    color: exp.tone === 'red' ? 'var(--danger)' : exp.tone === 'amber' ? 'var(--warn-fg)' : undefined,
    fontWeight: exp.tone === 'red' || exp.tone === 'amber' ? 600 : undefined,
  }}>{exp.status === 'expired' ? 'Expired ' : ''}{fmtQualDate(assignment.expiresOn)}</span></>}</>;
}

function ManagePersonDialog({ person, area, entry, onAdd, onEditExpiration, onClose }: {
  person: QualificationPerson; area: QualificationArea; entry?: PersonAreaAssignments;
  onAdd: () => void; onEditExpiration: (a: QualificationAssignment) => void; onClose: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const remove = async (id: string) => {
    setBusy(true); setError('');
    try { await revokeQualification(id); }
    catch (e) { setError(e instanceof Error ? e.message : 'Unable to remove assignment.'); }
    finally { setBusy(false); }
  };
  return <Modal title={`Manage ${person.displayName}`} onClose={onClose}>
    {error && <p role="alert">{error}</p>}
    {(['role', 'qualification'] as const).map(kind => <section key={kind}>
      <h4>{kind === 'role' ? 'Area Role' : 'Qualifications'}</h4>
      {(kind === 'role' ? entry?.roles : entry?.qualifications)?.map(a => <div key={a.id} className="set-row" style={{ flexWrap: 'wrap', gap: 8 }}>
        <span><AssignmentLine label={kind === 'role' ? roleTypeName(area, a.typeId) : qualificationTypeName(area, a.typeId)} assignment={a} /></span>
        <div style={{ display: 'flex', gap: 12 }}>
          {(kind === 'qualification' || findRoleType(area, a.typeId)?.requiresExpiration) &&
            <button className="linkbtn" disabled={busy} onClick={() => onEditExpiration(a)}>Edit expiration</button>}
          <button className="linkbtn" disabled={busy} onClick={() => void remove(a.id)}>Remove</button>
        </div>
      </div>)}
    </section>)}
    <p className="subtitle">One Area Role per person in this area. To change a role, remove it before adding another.</p>
    <div className="actions">
      <button className="btn" disabled={busy} onClick={onClose}>Done</button>
      <button className="btn primary" disabled={busy} onClick={onAdd}>Add role or qualification</button>
    </div>
  </Modal>;
}

// ---- Add / grant dialogs --------------------------------------------------

function AreaDialog({
  title,
  initialName,
  initialDescription,
  onApply,
  onClose,
}: {
  title: string;
  initialName: string;
  initialDescription: string;
  onApply: (name: string, description: string) => Promise<void>;
  onClose: () => void;
}) {
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!name.trim() || busy) return;
    setBusy(true);
    try {
      await onApply(name.trim(), description.trim());
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={title} onClose={onClose}>
      <label>Area name</label>
      <input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. NCAP" />
      <label>Description (optional)</label>
      <input
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="A short description of this program area"
      />
      <div className="actions">
        <button className="btn" onClick={onClose} disabled={busy}>
          Cancel
        </button>
        <button className="btn primary" onClick={() => void save()} disabled={!name.trim() || busy}>
          Save
        </button>
      </div>
    </Modal>
  );
}

function AddPersonDialog({
  areaName,
  people,
  onPick,
  onClose,
}: {
  areaName: string;
  /** Already filtered to exclude people already on this area's roster. */
  people: QualificationPerson[];
  onPick: (person: QualificationPerson) => void;
  onClose: () => void;
}) {
  const [query, setQuery] = useState('');
  const matches = query.trim()
    ? people.filter((c) => c.displayName.toLowerCase().includes(query.trim().toLowerCase())).slice(0, 8)
    : [];

  return (
    <Modal title={`Add person to ${areaName}`} onClose={onClose}>
      <label>Search people</label>
      <input
        autoFocus
        placeholder="Search people to add…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      {query.trim() && (
        <div style={{ marginTop: 4 }}>
          {matches.map((c) => (
            <button key={c.personId} type="button" className="people-option" onClick={() => onPick(c)}>
              {c.displayName}
              {people.filter(p => p.displayName.toLowerCase() === c.displayName.toLowerCase()).length > 1 &&
                <small> · {c.personId.slice(0, 8)} · select to review unit/Position context</small>}
            </button>
          ))}
          {matches.length === 0 && (
            <div className="people-option-empty">No matches, or already in this area.</div>
          )}
        </div>
      )}
      <div className="actions">
        <button className="btn" onClick={onClose}>
          Cancel
        </button>
      </div>
    </Modal>
  );
}

function GrantDialog({
  area,
  personId,
  personName,
  heldRoleIds,
  heldQualificationIds,
  onClose,
}: {
  area: QualificationArea;
  personId: string;
  personName: string;
  heldRoleIds: Set<string>;
  heldQualificationIds: Set<string>;
  onClose: () => void;
}) {
  const [personContext, setPersonContext] = useState<QualificationPerson | null>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let cancelled = false;
    setPersonContext(null);
    void qualificationPersonContext(personId).then(person => { if (!cancelled) setPersonContext(person); })
      .catch(e => { if (!cancelled) setError(e instanceof Error ? e.message : 'Unable to verify Person.'); });
    return () => { cancelled = true; };
  }, [personId]);
  const availableRoles = heldRoleIds.size ? [] : area.roleTypes;
  const availableQualifications = area.qualificationTypes.filter((t) => !heldQualificationIds.has(t.id));

  const initialKind: 'role' | 'qualification' = availableRoles.length > 0 ? 'role' : 'qualification';
  const [kind, setKind] = useState<'role' | 'qualification'>(initialKind);
  const options = kind === 'role' ? availableRoles : availableQualifications;
  const [typeId, setTypeId] = useState(options[0]?.id ?? '');
  const [expiresOn, setExpiresOn] = useState('');
  const [busy, setBusy] = useState(false);

  const selectedRoleType = kind === 'role' ? area.roleTypes.find((t) => t.id === typeId) : undefined;
  const showsExpiration = kind === 'qualification' || !!selectedRoleType?.requiresExpiration;

  const changeKind = (next: 'role' | 'qualification') => {
    setKind(next);
    const nextOptions = next === 'role' ? availableRoles : availableQualifications;
    setTypeId(nextOptions[0]?.id ?? '');
  };

  const save = async () => {
    if (!typeId || busy || !personContext) return;
    setBusy(true);
    try {
      await grantQualification({
        areaId: area.id,
        personId,
        kind,
        typeId,
        expiresOn: showsExpiration && expiresOn ? expiresOn : null,
      });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unable to save assignment.');
    } finally {
      setBusy(false);
    }
  };

  if (availableRoles.length === 0 && availableQualifications.length === 0) {
    return (
      <Modal title={`Add to ${area.name}`} onClose={onClose}>
        <p className="subtitle">
          {personName} already holds every role and qualification defined for {area.name}.
        </p>
        <div className="actions">
          <button className="btn" onClick={onClose}>
            Close
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title={`Add to ${area.name}`} onClose={onClose}>
      <p className="subtitle" style={{ marginTop: 0 }}>
        {personName}
      </p>
      {error && <p role="alert">{error}</p>}
      {!personContext && !error && <Spinner label="Verifying person…" />}
      {personContext && <p className="subtitle">{personContext.context?.join(' · ') || 'No additional unit/Position context recorded.'} · Person {personId.slice(0, 8)}</p>}

      {availableRoles.length > 0 && availableQualifications.length > 0 && (
        <>
          <label>Type</label>
          <select value={kind} onChange={(e) => changeKind(e.target.value as 'role' | 'qualification')}>
            <option value="role">Area role</option>
            <option value="qualification">Qualification</option>
          </select>
        </>
      )}

      <label>{kind === 'role' ? 'Area role' : 'Qualification'}</label>
      <select value={typeId} onChange={(e) => setTypeId(e.target.value)}>
        {options.map((t) => (
          <option key={t.id} value={t.id}>
            {t.name}
          </option>
        ))}
      </select>

      {showsExpiration && (
        <>
          <label>Expires on (optional)</label>
          <input type="date" value={expiresOn} onChange={(e) => setExpiresOn(e.target.value)} />
        </>
      )}

      <div className="actions">
        <button className="btn" onClick={onClose} disabled={busy}>
          Cancel
        </button>
        <button className="btn primary" onClick={() => void save()} disabled={!typeId || busy || !personContext}>
          Add
        </button>
      </div>
    </Modal>
  );
}

function ExpirationDialog({
  assignment,
  onClose,
}: {
  assignment: QualificationAssignment;
  onClose: () => void;
}) {
  const [value, setValue] = useState(assignment.expiresOn ?? '');
  const [busy, setBusy] = useState(false);

  const save = async (next: string | null) => {
    if (busy) return;
    setBusy(true);
    try {
      await updateQualificationExpiration(assignment.id, next);
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Edit expiration date" onClose={onClose}>
      <label>Expires on</label>
      <input type="date" autoFocus value={value} onChange={(e) => setValue(e.target.value)} />
      <div className="actions">
        <button className="btn" onClick={onClose} disabled={busy}>
          Cancel
        </button>
        {assignment.expiresOn && (
          <button className="btn" onClick={() => void save(null)} disabled={busy}>
            Clear
          </button>
        )}
        <button className="btn primary" onClick={() => void save(value || null)} disabled={busy || !value}>
          Save
        </button>
      </div>
    </Modal>
  );
}

// ---- Type vocabulary management -------------------------------------------

function TypesManager({
  area,
  assignments,
}: {
  area: QualificationArea;
  assignments: QualificationAssignment[];
}) {
  const [addingRole, setAddingRole] = useState(false);
  const [addingQualification, setAddingQualification] = useState(false);

  const roleUsage = (id: string) => assignments.filter((a) => a.kind === 'role' && a.typeId === id).length;
  const qualUsage = (id: string) =>
    assignments.filter((a) => a.kind === 'qualification' && a.typeId === id).length;

  const removeRoleType = (id: string) =>
    void updateQualificationArea(area.id, { roleTypes: area.roleTypes.filter((t) => t.id !== id) });
  const removeQualificationType = (id: string) =>
    void updateQualificationArea(area.id, {
      qualificationTypes: area.qualificationTypes.filter((t) => t.id !== id),
    });

  return (
    <div style={{ padding: '12px 16px', borderTop: '1px solid var(--line)', background: 'var(--bg)' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        <div>
          <div className="row" style={{ marginBottom: 6 }}>
            <strong style={{ fontSize: 13 }}>Area roles</strong>
            <button className="linkbtn" onClick={() => setAddingRole(true)}>
              + Add role
            </button>
          </div>
          <div className="set-list">
            {area.roleTypes.map((t) => {
              const refs = roleUsage(t.id);
              return (
                <div key={t.id} className="set-row">
                  <div className="set-row-main">
                    <div className="set-name">{t.name}</div>
                    <div className="set-refs">
                      {t.requiresExpiration ? 'Tracks expiration · ' : ''}
                      {refs} holder{refs === 1 ? '' : 's'}
                    </div>
                  </div>
                  <div className="set-row-actions">
                    {refs > 0 ? (
                      <span className="set-inuse">In use</span>
                    ) : (
                      <button className="linkbtn danger" onClick={() => removeRoleType(t.id)}>
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
            {area.roleTypes.length === 0 && <div className="set-row set-empty">None yet.</div>}
          </div>
        </div>

        <div>
          <div className="row" style={{ marginBottom: 6 }}>
            <strong style={{ fontSize: 13 }}>Qualifications</strong>
            <button className="linkbtn" onClick={() => setAddingQualification(true)}>
              + Add qualification
            </button>
          </div>
          <div className="set-list">
            {area.qualificationTypes.map((t) => {
              const refs = qualUsage(t.id);
              return (
                <div key={t.id} className="set-row">
                  <div className="set-row-main">
                    <div className="set-name">{t.name}</div>
                    <div className="set-refs">
                      {refs} holder{refs === 1 ? '' : 's'}
                    </div>
                  </div>
                  <div className="set-row-actions">
                    {refs > 0 ? (
                      <span className="set-inuse">In use</span>
                    ) : (
                      <button className="linkbtn danger" onClick={() => removeQualificationType(t.id)}>
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
            {area.qualificationTypes.length === 0 && <div className="set-row set-empty">None yet.</div>}
          </div>
        </div>
      </div>

      {addingRole && (
        <AddRoleTypeDialog
          onApply={async (name, requiresExpiration) => {
            await updateQualificationArea(area.id, {
              roleTypes: [...area.roleTypes, { id: newId(), name, requiresExpiration }],
            });
          }}
          onClose={() => setAddingRole(false)}
        />
      )}
      {addingQualification && (
        <AddNameDialog
          title="Add qualification"
          onApply={async (name) => {
            await updateQualificationArea(area.id, {
              qualificationTypes: [...area.qualificationTypes, { id: newId(), name }],
            });
          }}
          onClose={() => setAddingQualification(false)}
        />
      )}
    </div>
  );
}

function AddNameDialog({
  title,
  onApply,
  onClose,
}: {
  title: string;
  onApply: (name: string) => Promise<void>;
  onClose: () => void;
}) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!name.trim() || busy) return;
    setBusy(true);
    try {
      await onApply(name.trim());
      onClose();
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title={title} onClose={onClose}>
      <label>Name</label>
      <input
        autoFocus
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && name.trim()) void save();
        }}
      />
      <div className="actions">
        <button className="btn" onClick={onClose} disabled={busy}>
          Cancel
        </button>
        <button className="btn primary" onClick={() => void save()} disabled={!name.trim() || busy}>
          Add
        </button>
      </div>
    </Modal>
  );
}

function AddRoleTypeDialog({
  onApply,
  onClose,
}: {
  onApply: (name: string, requiresExpiration: boolean) => Promise<void>;
  onClose: () => void;
}) {
  const [name, setName] = useState('');
  const [requiresExpiration, setRequiresExpiration] = useState(false);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!name.trim() || busy) return;
    setBusy(true);
    try {
      await onApply(name.trim(), requiresExpiration);
      onClose();
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title="Add area role" onClose={onClose}>
      <label>Name</label>
      <input
        autoFocus
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="e.g. Council NCAP Chair"
      />
      <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
        <input
          type="checkbox"
          checked={requiresExpiration}
          onChange={(e) => setRequiresExpiration(e.target.checked)}
          style={{ width: 'auto' }}
        />
        This role tracks an expiration date
      </label>
      <div className="actions">
        <button className="btn" onClick={onClose} disabled={busy}>
          Cancel
        </button>
        <button className="btn primary" onClick={() => void save()} disabled={!name.trim() || busy}>
          Add
        </button>
      </div>
    </Modal>
  );
}
